import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, DrawerBody, DrawerFooter, Tooltip } from '@chakra-ui/react';
import moment from 'moment-timezone';

import { fieldInputTypes } from '../../../../../../../../../../../constants/fieldInputTypes';
import { inAppText } from '../../../../../../../../../../../constants/inAppText';
import { TabNames } from '../../../../../../../../../../../constants/tabMenuTypes';
import { selectCompanyId } from '../../../../../../../../../../../redux/modules/company';
import { hideTray, showTray } from '../../../../../../../../../../../redux/modules/tray';
import RequirementTemplateBlueprintType from '../../../../../../../../../../../types/RequirementTemplateBlueprintType';
import { REQUIREMENT_TYPES } from '../../../../../../../../../util/options';
import { getCreateRequirementTemplateBlockParams, getPolicyProperties } from '../../../../../../../../../util/util';
import SetPolicyFieldValuesTray from '../../SetPolicyFieldValuesTray';

import CustomPolicyTypeForm from './CustomPolicyTypeForm';

const CreateCustomPolicyTypeForm = ({ blueprint }) => {
  const dispatch = useDispatch();
  const companyId = useSelector(selectCompanyId);
  const [isSubmitting, setSubmitting] = useState(false);
  const [showError, setShowError] = useState(false);

  const currentRequirementTemplateBlocks = useSelector(blueprint.selectCurrentRequirementTemplateBlocks);
  const currentEditCustomPolicyId = useSelector(blueprint.selectCurrentEditCustomPolicyId);
  const selectedPropertyFieldTypes = useSelector(blueprint.selectSelectedPolicyPropertyFieldTypes);

  let currentMaxOrder = 0;

  useEffect(() => {
    if (currentRequirementTemplateBlocks) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      currentMaxOrder = Math.max(...[0,
        ...currentRequirementTemplateBlocks.map(templateBlock => templateBlock.order)]);
    }
  }, [currentRequirementTemplateBlocks, dispatch]);

  const onSubmit = async (data, setError) => {
    setSubmitting(true);
    if (showError) setShowError(false);

    const newCustomPolicyInfo = {
      name: data?.name,
      description: data?.description,
      display_name: data.policyName,
      properties: getPolicyProperties(data, companyId)
    };

    const { properties } = newCustomPolicyInfo;
    const uniqueProperties = properties
      .filter((value, index, self) => self
        .findIndex(v => v.display_name.toLowerCase() === value.display_name.toLowerCase()) === index)
      .map(el => el);

    if (uniqueProperties.length !== properties.length) {
      setShowError(true);
      setSubmitting(false);

      const duplicatePropertyFields = properties.filter(p => !uniqueProperties.includes(p))
        .map(el => {
          const dataType = el.data_type === 'string' ? fieldInputTypes.AlphaNumeric : el.data_type;
          const fieldIndex = data[dataType].findIndex(f => f.label === el.display_name);
          return `${dataType}.${fieldIndex}.label`;
        });

      duplicatePropertyFields.forEach(field => {
        setError(field, { type: 'custom', message: 'Property names must be unique' });
      });

      return;
    }

    if (data?.addToList === false) {
      newCustomPolicyInfo.deleted_at = moment().format('YYYY-MM-DD HH:mm:ss');
    }

    let response = null;

    if (!currentEditCustomPolicyId) {
      response = await dispatch(blueprint.createInsurancePolicyType(newCustomPolicyInfo));
    } else {
      response = await dispatch(blueprint.updateInsurancePolicyType(currentEditCustomPolicyId, newCustomPolicyInfo));
    }

    if (response && !currentEditCustomPolicyId) {
      const templateBlockResponse = await dispatch(blueprint.createRequirementTemplateBlock(
        getCreateRequirementTemplateBlockParams(
          currentMaxOrder,
          REQUIREMENT_TYPES.Policies,
          { properties: [] }
        )
      ));

      dispatch(blueprint.listInsurancePolicyTypes());
      if (templateBlockResponse) {
        await dispatch(blueprint.setCurrentPolicyType(response));
        setSubmitting(false);
        dispatch(blueprint.resetCreateCustomPolicyType());
        dispatch(blueprint.setCreateCustomStep(1, TabNames.Policies));
        dispatch(blueprint.setCurrentRequirementTemplateBlock(templateBlockResponse.id));
        dispatch(showTray(
          () => <SetPolicyFieldValuesTray blueprint={blueprint} currentPolicyTypeId={response.id} />,
          {
            onClose: async () => {
              const currentTemplateBlock = templateBlockResponse;
              if (!currentTemplateBlock.length
                      || !currentTemplateBlock?.requirementTemplateBlockProperties.filter(p => p.value).length) {
                await dispatch(blueprint.deleteRequirementTemplateBlock(currentTemplateBlock?.id));
              }

              dispatch(blueprint.setCurrentRequirementTemplateBlock(null));
              dispatch(hideTray());
            }
          }
        ));
      }
    } else {
      setSubmitting(false);
      dispatch(blueprint.resetCreateCustomPolicyType());
      dispatch(blueprint.setCreateCustomStep(1, TabNames.Policies));
      dispatch(hideTray());
    }
  };

  return (
    <>
      <DrawerBody>
        <CustomPolicyTypeForm blueprint={blueprint} formId="createCustomPolicyTypeForm" onSubmit={onSubmit} />
      </DrawerBody>
      <DrawerFooter>
        <Tooltip
          hasArrow
          isDisabled={isSubmitting || !!selectedPropertyFieldTypes.length}
          label={inAppText.tooltips.templates.templateBuilder.createCustomPolicy.saveButtonDisabled}
        >
          <Box width="100%">
            <Button
              form="createCustomPolicyTypeForm"
              isDisabled={isSubmitting || !selectedPropertyFieldTypes.length}
              type="submit"
              width="100%"
            >
              Save Policy
            </Button>
          </Box>
        </Tooltip>
      </DrawerFooter>
    </>
  );
};

CreateCustomPolicyTypeForm.propTypes = { blueprint: RequirementTemplateBlueprintType };
export default CreateCustomPolicyTypeForm;
